/**
 * Accounts Receivable shared types
 */

/**
 * Source of a sales order - where it originated from
 */
export enum SalesOrderSource {
  Edi = "edi",
  Email = "email",
  DirectEntry = "direct_entry",
  FileUpload = "file_upload",
  ErpSync = "erp_sync",
}

/**
 * Status of a staged sales order (draft/review state)
 * These are orders that have been received but not yet approved
 */
export enum StagedPurchaseOrderStatus {
  RECEIVED = "RECEIVED",
  // PENDING_REVIEW = "PENDING_REVIEW",
  APPROVED = "APPROVED",
  REJECTED = "REJECTED",
  PENDING_CHANGES = "PENDING_CHANGES",
}

/**
 * Lifecycle stage of a sales order as it moves through the approval funnel,
 * from inbound receipt to ERP submission. Persisted as discrete transition
 * events in `ar_order_stage_events` (one row per stage entry per staged PO).
 *
 * An order may SKIP the discount and/or order-approval stages (no discounts
 * applied, or no order approval required). The AWAITING_* stages can re-enter
 * (e.g. after an order approver requests changes). RECEIVED, SUBMITTED_TO_ERP
 * and REJECTED are once-only terminal-ish markers.
 *
 * SUBMITTED_TO_ERP is intentionally ERP-agnostic — it covers Oracle Fusion,
 * Oracle EBS and Famous (whichever ERP the org syncs to).
 */
export enum OrderStage {
  Received = "RECEIVED",
  AwaitingDiscountApproval = "AWAITING_DISCOUNT_APPROVAL",
  AwaitingOrderApproval = "AWAITING_ORDER_APPROVAL",
  SubmittedToErp = "SUBMITTED_TO_ERP",
  Rejected = "REJECTED",
}

/**
 * Coarse, v3-native lifecycle position of a draft sales order, rolled up for
 * the review-page stepper and the list badge. Derived purely from the v3 draft
 * (its `status`, `salesOrderId`, and recorded approval decisions in
 * `rawData.approvals`) — NOT the legacy `ar_order_stage_events` funnel, which
 * is keyed by the staged-PO id (a different model the v3 path does not use).
 *
 * `rejected` is a branch off the happy path; the rest are sequential:
 * `draft → creating_discounts → approving → ready → created`.
 *
 * `creating_discounts` is the stage an order sits in when it has line(s) priced
 * below list with no discount created yet — the reviewer's next step is to
 * create the discounts that the order needs before it can move into approval.
 */
export type OrderLifecycleStage =
  | "draft"
  | "creating_discounts"
  | "approving"
  | "ready"
  | "created"
  | "rejected";

/** Happy-path stepper sequence (rejected is a branch, not a step). */
export const ORDER_LIFECYCLE_STAGE_SEQUENCE: OrderLifecycleStage[] = [
  "draft",
  "creating_discounts",
  "approving",
  "ready",
  "created",
];

export const ORDER_LIFECYCLE_STAGE_LABELS: Record<OrderLifecycleStage, string> =
  {
    draft: "Draft",
    creating_discounts: "Creating Discounts",
    approving: "Approving",
    ready: "Ready",
    created: "Created",
    rejected: "Rejected",
  };

export interface LifecycleStageInput {
  /** The draft's `StagedPurchaseOrderStatus` value. */
  status: string;
  /** Set once the order has been created in the ERP. */
  salesOrderId?: string | null;
  /**
   * Recorded approval-gate decisions from `rawData.approvals`, if loaded.
   * Only decided gates are stored, so this distinguishes a partially-approved
   * order from a fresh draft.
   */
  approvalDecisions?: ReadonlyArray<{ decision: string }>;
  /**
   * True when the order has an approval gate actively awaiting a decision
   * (a blocking run in review, or an active approval node with no decision).
   * Lets the stage show `approving` even before any decision is recorded.
   */
  hasActiveApproval?: boolean;
  /**
   * True when a line carries a discount that's pending approval (the spec's
   * "awaiting discount approval" funnel state). Also surfaces as `approving`.
   */
  hasPendingDiscountApproval?: boolean;
  /**
   * True when the order has line(s) priced below list with no discount created
   * yet — i.e. the order still needs discounts before it can move to approval.
   * Surfaces the `creating_discounts` stage. Distinct from
   * `hasPendingDiscountApproval` (a discount exists and is awaiting approval).
   */
  needsDiscounts?: boolean;
}

/**
 * Roll a v3 draft up to a single {@link OrderLifecycleStage}. Pure — shared by
 * the backend list/detail handlers and the frontend stepper so they never
 * disagree. Precedence:
 * created → rejected → ready → approving → creating_discounts → draft.
 */
export function rollupLifecycleStage(
  input: LifecycleStageInput,
): OrderLifecycleStage {
  if (input.salesOrderId) return "created";
  const decisions = input.approvalDecisions ?? [];
  const anyRejected = decisions.some((d) => d.decision === "rejected");
  if (input.status === StagedPurchaseOrderStatus.REJECTED || anyRejected) {
    return "rejected";
  }
  if (input.status === StagedPurchaseOrderStatus.APPROVED) return "ready";
  // In approval: a gate is actively awaiting a decision, a discount is pending
  // approval, or at least one gate decided but the order isn't fully approved.
  if (
    input.hasActiveApproval ||
    input.hasPendingDiscountApproval ||
    decisions.length > 0
  ) {
    return "approving";
  }
  // Pre-approval but the order still needs discounts created (below-list lines
  // with no discount yet) — the reviewer's next step before approval.
  if (input.needsDiscounts) return "creating_discounts";
  return "draft";
}

// ---------------------------------------------------------------------------
// Pending Change Types (860 PO Change Request for approved orders)
// ---------------------------------------------------------------------------

export type PendingChangeDecision = "pending" | "approved" | "denied";
export type PendingChangeType = "update" | "delete" | "add";

export interface PendingLineItemChange {
  /** Index into the current lineItems array (-1 for adds) */
  originalLineItemIndex: number;
  changeType: PendingChangeType;
  /** Snapshot of original values before the change (null for adds) */
  originalValues: Record<string, unknown> | null;
  /** Proposed new values from the 860 */
  proposedValues: Record<string, unknown>;
  /** User decision */
  decision: PendingChangeDecision;
  /** Index into orderful860Payloads for traceability */
  edi860PayloadIndex: number;
}

export interface PendingHeaderChange {
  /** Field name on the staged purchase order (e.g. "deliveryDate") */
  field: string;
  /** Human-readable label */
  label: string;
  /** Current value */
  originalValue: unknown;
  /** Proposed new value from the 860 */
  proposedValue: unknown;
  /** User decision */
  decision: PendingChangeDecision;
  /** Index into orderful860Payloads for traceability */
  edi860PayloadIndex: number;
}
